import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { secureGet, secureDelete, api, securePost } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";

import { DoubleButton } from '../../components/PrimaryButton';
import ProductCard from './ProductCard';
import OptionsMenu from '../../components/OptionsMenu';
import ProductPopup from './ProductPopup';
import Filter from '../../components/Filter';
import Table from '../../components/Table';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import ModuleWrapper from '../../components/ModuleWrapper';
import UserContext from '../../Shared/UserContext';
import LoadingLogo from '../../components/LoadingLogo';
import SnackbarNotification from '../../components/SnackbarNotification';

import EditIcon from '../../icons/edit.svg';
import DeleteIcon from '../../icons/trashR.svg';
import CardViewIcon from '../../icons/grid.svg';
import TableViewIcon from '../../icons/table.svg';

const Products = () => {
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [filterValues, setFilterValues] = useState({
    sortBy: "",
    sortDirection: "asc",
    brandId: '',
    familyId: '',
    needle: '',
    onSale: '',
    availableOnly: false,
  });
  const [filterOptions, setFilterOptions] = useState({ brands: [], families: [] });
  const [pageSize, setPageSize] = useState(30);
  const [isTableView, setIsTableView] = useState(false);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);
  const [, setCurrentPage] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { t } = useTranslation();
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const queryParams = new URLSearchParams(location.search);
  const brandId = Number(queryParams.get('brand'));
  const familyId = Number(queryParams.get('family'));

  const columns = [
    {
      label: (
        <div className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("reference")}>
          {t(`columns.reference`)}
          {filterValues.sortBy === "reference" && (
            <span className="ml-1">{filterValues.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "reference",
      render: (_, row) =>
        row?.reference || "N/A",
    },
    {
      label: (
        <div className="flex items-center cursor-pointer"
          onClick={() => handleSortChange("name")}>
          {t(`columns.name`)}
          {filterValues.sortBy === "name" && (
            <span className="ml-1">{filterValues.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "name",
      render: (_, row) => row.name || "N/A"
    },
    {
      label: (
        <div className="flex items-center cursor-pointer" onClick={() => handleSortChange("brand.name")}>
          {t(`columns.brand`)}
          {filterValues.sortBy === "brandName" && (
            <span className="ml-1">{filterValues.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "brandName",
      render: (_, row) => row.brand?.name || "N/A"
    },
    {
      label: (
        <div className="flex items-center cursor-pointer" onClick={() => handleSortChange("family.name")}>
          {t(`columns.family`)}
          {filterValues.sortBy === "familyName" && (
            <span className="ml-1">{filterValues.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "familyName",
      render: (_, row) =>
        row.family?.name || "N/A"
    },
    { label: 'Prix HT', key: 'priceHT' },
    {
      label: (
        <div className="flex items-center cursor-pointer" onClick={() => handleSortChange("priceTTC")}>
          Prix TTC
          {filterValues.sortBy === "priceTTC" && (
            <span className="ml-1">{filterValues.sortDirection === "asc" ? "▲" : "▼"}</span>
          )}
        </div>
      ),
      key: "priceTTC"
    },
    {
      label: t(`columns.discount`),
      key: 'discount',
      render: (value) => value != null ? `${value}%` : ""
    }
  ];

  if (role === 'ROLE_ADMIN') {
    columns.push({
      key: 'actions',
      render: (_, row) => (
        <div className="flex gap-2 ">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }} className="flex items-center justify-center w-10 h-10 bg-white rounded-[12px] shadow cursor-pointer hover:bg-gray-100"
          >
            <img src={EditIcon} alt="Modifier" className="w-4 h-4" />
          </button>

          <button
            onClick={() => handleDeleteClick(row)}
            className="flex items-center justify-center w-10 h-10 bg-white rounded-[12px] shadow cursor-pointer hover:bg-gray-100"
          >
            <img src={DeleteIcon} alt="Supprimer" className="w-4 h-4" />
          </button>
        </div>
      ),
    });
  }


  const resetFilters = () => {
    const newFilterValues = { brandId: '', familyId: '', needle: '', sortBy: '', onSale: '' };
    setFilterValues(newFilterValues);
    fetchProducts(0, pageSize, newFilterValues);
  };


  const fetchProducts = useCallback(async (page = 0, size = pageSize, filters = filterValues) => {
    setLoading(true);
    setError(null);
    try {
      const requestBody = {
        availableOnly: filters.availableOnly || false,
        brandId: filters.brandId || null,
        familyId: filters.familyId || null,
        needle: filters.needle || "",
        onSale: filters.onSale,
      };


      const sortParam = filters.sortBy ? `&sort=${filters.sortBy},${filters.sortDirection || 'asc'}` : '';
      const response = await securePost(`/products?page=${page}&size=${size}${sortParam}`, requestBody, showSnackbar, { signal });
      const { content, totalPages } = response.data;
      setDisplayedProducts(content);
      setTotalPages(totalPages);
      setPageNumber(page);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [filterValues, pageSize, signal]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const brandsResponse = await secureGet('/brands', showSnackbar, { signal });
        setFilterOptions((prev) => ({
          ...prev,
          brands: brandsResponse.data.map((item) => ({ label: item.name, value: item.id })),
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching brands:', error);
        }
      }
    };

    const fetchFamilies = async () => {
      try {
        const familiesResponse = await secureGet('/families', showSnackbar, { signal });
        setFilterOptions((prev) => ({
          ...prev,
          families: familiesResponse.data.map((item) => ({ label: item.name, value: item.id })),
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching families:', error);
        }
      }
    };

    fetchBrands();
    fetchFamilies();

    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  useEffect(() => {
    const newFilters = { brandId: brandId || '', familyId: familyId || '' };
    setFilterValues(newFilters);
    fetchProducts(0, pageSize, newFilters);
  }, [location.search, pageSize, brandId, familyId]);

  const handleFilterChange = (name, value) => {
    if (name === 'toggleView') {
      toggleView();
    } else {
      const updatedFilters = { ...filterValues, [name]: value };
      setFilterValues(updatedFilters);
      setCurrentPage(0);
      fetchProducts(0, pageSize, updatedFilters);
    }
  };

  const handleAddClick = () => {
    setSelectedProductForEdit(null);
    setShowModule(true);
  };

  const handleEditClick = (product) => {
    setSelectedProductForEdit(product);
    setShowModule(true);
  };

  const handleModuleClose = (updatedProduct) => {
    if (updatedProduct) {
      if (updatedProduct.name) {
        setDisplayedProducts((prev) => [updatedProduct, ...prev]);
      } else {
        fetchProducts(pageNumber);
      }
    } else {
    }
    setShowModule(false);
    setSelectedProductForEdit(null);
  };

  const getOptions = (item) => [
    {
      label: t(`actions.modify`),
      icon: EditIcon,
      action: () => handleEditClick(item),
    },
    {
      label: t(`actions.delete`),
      color: "#EF476F",
      icon: DeleteIcon,
      action: () => { handleDeleteClick(item) },
    },
  ];

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const deleteUrl = `/products/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl, showSnackbar);

      setDisplayedProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== itemToDelete.id)
      );

      fetchProducts(pageNumber, filterValues);
      showSnackbar(t(`confirmation.productDelete`), 'success');

    } catch (error) {
      setDeletePopupVisible(false);
      console.error(Error` deleting product with ID: ${itemToDelete.id}, error`);
    } finally {
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleSortChange = (field) => {
    const newDirection = filterValues.sortBy === field
      ? (filterValues.sortDirection === "asc" ? "desc" : "asc")
      : "asc";

    setFilterValues((prev) => ({
      ...prev,
      sortBy: field,
      sortDirection: newDirection,
    }));

    fetchProducts(0, pageSize, {
      ...filterValues,
      sortBy: field,
      sortDirection: newDirection,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProducts(page, pageSize, filterValues);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    // fetchProducts(0, size, filterValues);
  };

  const toggleView = () => {
    setIsTableView((prev) => !prev);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {

      const fileUploadResponse = await api.post(
        '/products/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('token'),
          },
        }
      );
      fetchProducts();
      showSnackbar(t(`confirmation.upload`), 'success');

    } catch (error) {
      console.error("File upload failed:", error);
      showSnackbar(t('errors.generic'), 'error');
    }
  };

  return (
    <div className="relative">
      <Helmet>
        <title>GrayFox - {t("titles.products")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.products`)}</h1>

        <div className="ml-auto text-[16px] gap-3 flex">
          {role === "ROLE_ADMIN" && (
            <DoubleButton
              manual={t(`primaryButtons.add_product`)}
              upload={t(`primaryButtons.upload_product`)}
              onManualClick={handleAddClick}
              onUploadClick={handleUpload}
            />
          )}
        </div>
      </div>

      <Filter
        filters={[
          { name: "needle", type: "search" },
          { name: "brandId", type: "dropdown", label: t(`filters.products.brand`), options: filterOptions.brands },
          { name: "familyId", type: "dropdown", label: t(`filters.products.family`), options: filterOptions.families },
          {
            name: "sortBy",
            type: "dropdown",
            label: t(`filters.products.sortBy`),
            options: [
              { label: t(`options.reference`), value: "reference" },
              { label: t(`options.name`), value: "name" },
              { label: t(`options.brandName`), value: "brand.name" },
              { label: t(`options.familyName`), value: "family.name" },
              { label: t(`options.priceTTC`), value: "priceTTC" },
            ],
          },
          {
            name: "onSale",
            type: "dropdown",
            label: t(`filters.products.filterBy`),
            options: [
              { label: t(`options.all`), value: "false" },
              { label: t(`options.onSales`), value: "true" },
            ],
          },
          {
            name: "toggleView",
            type: "toggle",
            label: "",
            options: [
              { value: "card", icon: TableViewIcon, tooltip: "Afficher dans un tableau" },
              { value: "table", icon: CardViewIcon, tooltip: "Afficher sous forme de cartes" },
            ],
            selectedValue: isTableView ? "card" : "table",
          },
        ]}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={{ ...filterValues, toggleView: isTableView ? "table" : "card" }}
      />

      <div className="relative">
        {error && (
          <p className="text-red-500 text-center">{error}</p>
        )}

        {isTableView ? (
          <div style={{ position: "relative", minHeight: "200px" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1,
                }}
              >
                <LoadingLogo />
              </div>
            )}

            <Table
              columns={columns}
              data={loading ? [] : displayedProducts}
              onRowClick={(row) => handleProductClick(row.id)}
            />

            {!loading && displayedProducts.length === 0 && (
              <div className="flex justify-center py-8">
                <p className="text-gray-500"></p>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 relative">
            {loading ? (
              <div className="col-span-full flex justify-center">
                <LoadingLogo />
              </div>
            ) : displayedProducts.length > 0 ? (
              displayedProducts.map((item) => (
                <div key={item.id} style={{ position: "relative" }}>
                  <ProductCard
                    image={item.image}
                    familyName={item.family?.name || "Unknown Family"}
                    name={item.name}
                    price={item.priceTTC}
                    reference={item.reference}
                    onClick={() => handleProductClick(item.id)}
                    optionsMenu={
                      <div onClick={(e) => e.stopPropagation()}>
                        {role === "ROLE_ADMIN" && <OptionsMenu options={getOptions(item)} />}
                      </div>
                    }
                  />
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center w-full"></p>
            )}
          </div>

        )}
      </div>


      {displayedProducts.length > 0 && (
        <PageScroller
          pageNumber={pageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {selectedProduct && (
        <ProductPopup
          productId={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="products"
          mode={selectedProductForEdit ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedProductForEdit || {}}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
}

export default Products;



